import { useRef, useEffect, useState } from "react";

import { TooltipBase } from "@frontend/common";
import { splitMultipleStringList } from "@frontend/common/src/business/DatabaseTable/utils/splitMultipleStringList";

import { PatentListData } from "@/components/business/PatentContainer/Patent.types";
import LabelingPressTitle from "@/components/shared/ui/LabelingPressTitle/LabelingPressTitle";

import * as S from "./PatentTableShellForPatentTitle.style";

interface Props {
  patentData: PatentListData;
  onClickMove: () => void;
}

const PatentTableShellForPatentTitle = ({ patentData, onClickMove }: Props) => {
  const [isTruncated, setIsTruncated] = useState(false);
  const titleRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const checkTruncation = () => {
      if (titleRef.current) {
        const { scrollHeight, clientHeight } = titleRef.current;
        setIsTruncated(scrollHeight > clientHeight + 1);
      }
    };

    setTimeout(checkTruncation, 0);
    window.addEventListener("resize", checkTruncation);

    return () => {
      window.removeEventListener("resize", checkTruncation);
    };
  }, [patentData?.title]);

  const title = patentData?.title || "-";
  const titleList = splitMultipleStringList(title).filter(Boolean);
  const isMultipleTitle = title.includes("__");
  const visibleTitleList = titleList.slice(0, 2);
  const remainingTitleCount = titleList.length > 2 ? titleList.length - 2 : 0;
  const hasTitleTooltip =
    isTruncated || (isMultipleTitle && remainingTitleCount > 0);

  const renderTitleBulletList = (items: string[]) => {
    return (
      <S.PatentTitleBulletList>
        {items.map((item) => (
          <S.PatentTitleBulletItem key={`${item}-${title}`}>
            <S.PatentTitleBulletItemText>
              <LabelingPressTitle title={item} />
            </S.PatentTitleBulletItemText>
          </S.PatentTitleBulletItem>
        ))}
      </S.PatentTitleBulletList>
    );
  };

  const renderTitle = () => {
    if (!isMultipleTitle) {
      return <LabelingPressTitle title={title} />;
    }

    if (titleList.length === 0) {
      return <LabelingPressTitle title="-" />;
    }

    return (
      <>
        {renderTitleBulletList(visibleTitleList)}

        {remainingTitleCount > 0 && (
          <S.PatentTitleNumber>+{remainingTitleCount}</S.PatentTitleNumber>
        )}
      </>
    );
  };

  const renderTooltipTitle = () => {
    if (!isMultipleTitle || titleList.length === 0) {
      return <LabelingPressTitle title={title} />;
    }

    return renderTitleBulletList(titleList);
  };

  return (
    <S.PatentTitleTableShell onClick={onClickMove}>
      <S.TitleWrapper>
        <div ref={titleRef}>{renderTitle()}</div>
      </S.TitleWrapper>
      {hasTitleTooltip && (
        <S.TooltipWrapper className="tooltip-container">
          <TooltipBase className="title-tooltip">
            {renderTooltipTitle()}
          </TooltipBase>
        </S.TooltipWrapper>
      )}
    </S.PatentTitleTableShell>
  );
};

export default PatentTableShellForPatentTitle;
